import {
  AngledHighwayElement,
  Element,
  GroupLogicalElement,
  HighwayElement,
  LinePathElement,
  RectCenteredElement,
} from '@thive/canvas';

import { FDDM_AREA_TAGS, FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { isIntersect } from '@/modules/common/helpers/array';

export const isRectCenteredElement = (element: Element): element is RectCenteredElement =>
  element.type === 'RectCentered';

export const isAreaCanvasElement = (
  element: Element,
): element is RectCenteredElement | HighwayElement | AngledHighwayElement =>
  isIntersect(element.tags ?? [], FDDM_AREA_TAGS) &&
  (element.type === 'RectCentered' ||
    element.type === 'Highway' ||
    element.type === 'AngledHighway');

export const isObstacleCanvasElement = (
  element: Element,
): element is RectCenteredElement | LinePathElement =>
  (element.tags?.includes(FDDM_TAG.OBSTACLE) || element.tags?.includes(FDDM_TAG.WALL)) === true &&
  (element.type === 'RectCentered' || element.type === 'LinePath');

export const isLogicalSpaceCanvasElement = (element: Element): element is RectCenteredElement =>
  element.tags?.includes(FDDM_TAG.LOGICAL_SPACE) === true && element.type === 'RectCentered';

export const isGroupCanvasElement = (element: Element): element is GroupLogicalElement =>
  element.tags?.includes(FDDM_TAG.GROUP) === true && element.type === 'GroupLogical';

export const isTemplateCanvasElement = (element: Element): element is GroupLogicalElement =>
  element.tags?.includes(FDDM_TAG.TEMPLATE) === true && element.type === 'GroupLogical';

type StableRectCenteredElement = RectCenteredElement &
  Required<Pick<RectCenteredElement, 'position' | 'size' | 'scale' | 'rotation'>>;

type StableLinePathElement = LinePathElement &
  Required<Pick<LinePathElement, 'position' | 'rotation' | 'points'>>;

type StableHighwayElement = HighwayElement &
  Required<Pick<HighwayElement, 'position' | 'scale' | 'rotation'>>;

type StableAngledHighwayElement = AngledHighwayElement &
  Required<Pick<AngledHighwayElement, 'position' | 'rotation' | 'points'>>;

export const isStableRectCenteredElement = (
  element: Element,
): element is StableRectCenteredElement => {
  if (element.type !== 'RectCentered') {
    return false;
  }
  if (!element.position) {
    return false;
  }
  if (!element.size) {
    return false;
  }
  if (!element.scale) {
    return false;
  }
  if (!element.rotation) {
    return false;
  }
  return true;
};

export const isStableLinePathElement = (element: Element): element is StableLinePathElement => {
  if (element.type !== 'LinePath') {
    return false;
  }
  if (!element.position) {
    return false;
  }
  if (!element.rotation) {
    return false;
  }
  if (!element.points || element.points.length < 2) {
    return false;
  }
  return true;
};

export const isStableHighwayElement = (element: Element): element is StableHighwayElement => {
  if (element.type !== 'Highway') {
    return false;
  }
  if (!element.position) {
    return false;
  }
  if (!element.scale) {
    return false;
  }
  if (!element.rotation) {
    return false;
  }
  return true;
};

export const isStableAngledHighwayElement = (
  element: Element,
): element is StableAngledHighwayElement => {
  if (element.type !== 'AngledHighway') {
    return false;
  }
  if (!element.position) {
    return false;
  }
  if (!element.rotation) {
    return false;
  }
  if (!element.points || element.points.length < 2) {
    return false;
  }
  return true;
};
